import React, { useState } from 'react';
import { X, AlertCircle, CheckCircle, Loader2, Sparkles } from 'lucide-react';
import { translations } from '../../translations';
import { supabase } from '../../lib/supabase';

interface CancellationModalProps {
  onClose: () => void;
  onConfirm: () => Promise<void>;
  isLoading: boolean;
  isDarkMode: boolean;
  language: string;
  subscriptionId?: string | null;
}

type CancellationReason = 
  | 'no_results' 
  | 'not_understood' 
  | 'budget' 
  | 'no_need' 
  | 'too_complicated' 
  | 'other';

export function CancellationModal({ 
  onClose, 
  onConfirm, 
  isLoading, 
  isDarkMode, 
  language,
  subscriptionId
}: CancellationModalProps) {
  const [step, setStep] = useState<1 | 2 | 3>(1);
  const [selectedReasons, setSelectedReasons] = useState<CancellationReason[]>([]);
  const [otherReason, setOtherReason] = useState('');
  const [isApplyingDiscount, setIsApplyingDiscount] = useState(false);
  const [discountError, setDiscountError] = useState<string | null>(null);
  const t = translations[language as keyof typeof translations];

  const handleReasonToggle = (reason: CancellationReason) => {
    setSelectedReasons(prev => 
      prev.includes(reason)
        ? prev.filter(r => r !== reason)
        : [...prev, reason]
    );
  };

  const handleContinue = () => {
    if (step === 1) {
      setStep(2);
    } else if (step === 2) {
      if (selectedReasons.length === 0) {
        // Require at least one reason
        return;
      }
      setStep(3);
    } else {
      onConfirm();
    }
  };

  const handleGoBack = () => {
    if (step === 2) {
      setStep(1);
    } else if (step === 3) {
      setStep(2);
    }
  };

  const handleApplyDiscount = async () => {
    if (!subscriptionId) {
      setDiscountError("Subscription ID not found");
      return;
    }

    try {
      setIsApplyingDiscount(true);
      setDiscountError(null);

      const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/stripe-apply-coupon`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          subscription_id: subscriptionId,
          promotion_code: 'FjybI8oh'
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to apply discount');
      }

      // Close modal and reload page to show updated subscription
      onClose();
      window.location.reload();
    } catch (error) {
      console.error('Error applying discount:', error);
      setDiscountError(error instanceof Error ? error.message : 'Failed to apply discount');
    } finally {
      setIsApplyingDiscount(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className={`w-full max-w-md rounded-lg p-6 ${
        isDarkMode ? 'bg-[#1c1c1c]' : 'bg-white'
      }`}>
        <div className="flex justify-between items-center mb-4">
          <h3 className={`text-xl font-semibold ${
            isDarkMode ? 'text-gray-200' : 'text-gray-900'
          }`}>
            {step === 1 && t.cancel_step1_title}
            {step === 2 && t.cancel_step2_title}
            {step === 3 && t.cancel_special_offer_title}
          </h3>
          <button 
            onClick={onClose}
            className={`p-1 rounded-full ${
              isDarkMode ? 'hover:bg-[#2b2b2b]' : 'hover:bg-gray-100'
            }`}
          >
            <X size={20} className={isDarkMode ? 'text-gray-400' : 'text-gray-500'} />
          </button>
        </div>
        
        {step === 1 && (
          <div className="space-y-4">
            <div className="flex items-start gap-3">
              <AlertCircle className="text-amber-500 mt-0.5 flex-shrink-0" size={20} />
              <div className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                <p className="font-medium mb-2">{t.cancel_long_term}</p>
                <ul className="space-y-1">
                  <li className="flex items-center gap-2">
                    <span className="text-red-500">❌</span>
                    <span>{t.cancel_lose_optimizations}</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="text-red-500">❌</span>
                    <span>{t.cancel_lose_progress}</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="text-red-500">❌</span>
                    <span>{t.cancel_lose_followers}</span>
                  </li>
                </ul>
                <img 
                  src="https://auth.amplr.ai/storage/v1/object/public/avatars/cancel/cancel.png"
                  alt="Cancel illustration"
                  className="w-full h-auto rounded-lg my-4"
                />
                <p className="mt-3">{t.cancel_restart_warning}</p>
              </div>
            </div>
            
            <div className="flex flex-col gap-3 mt-6">
              <button
                onClick={onClose}
                className="w-full py-2.5 bg-orange-500 text-white rounded-lg font-medium hover:bg-orange-600 transition-colors"
              >
                {t.cancel_keep_subscription}
              </button>
              
              <button
                onClick={() => setStep(2)}
                className={`w-full py-2.5 rounded-lg font-medium transition-colors ${
                  isDarkMode 
                    ? 'bg-[#2b2b2b] text-gray-300 hover:bg-[#333333]' 
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {t.cancel_proceed_anyway}
              </button>
            </div>
          </div>
        )}
        
        {step === 2 && (
          <div className="space-y-4">
            <p className={`text-sm mb-4 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
              {t.cancel_reason_prompt}
            </p>
            
            <div className="space-y-3">
              <label className={`flex items-center gap-3 p-3 rounded-lg cursor-pointer ${
                selectedReasons.includes('no_results')
                  ? 'bg-orange-50 border border-orange-200 text-orange-700'
                  : isDarkMode
                    ? 'bg-[#2b2b2b] text-gray-300'
                    : 'bg-gray-50 text-gray-700'
              }`}>
                <input
                  type="checkbox"
                  checked={selectedReasons.includes('no_results')}
                  onChange={() => handleReasonToggle('no_results')}
                  className="sr-only"
                />
                <div className={`w-5 h-5 flex-shrink-0 rounded-md border ${
                  selectedReasons.includes('no_results')
                    ? 'bg-orange-500 border-orange-500 flex items-center justify-center'
                    : isDarkMode
                      ? 'border-gray-600'
                      : 'border-gray-300'
                }`}>
                  {selectedReasons.includes('no_results') && (
                    <CheckCircle size={14} className="text-white" />
                  )}
                </div>
                <span className="text-sm">{t.cancel_reason_no_results}</span>
              </label>
              
              <label className={`flex items-center gap-3 p-3 rounded-lg cursor-pointer ${
                selectedReasons.includes('not_understood')
                  ? 'bg-orange-50 border border-orange-200 text-orange-700'
                  : isDarkMode
                    ? 'bg-[#2b2b2b] text-gray-300'
                    : 'bg-gray-50 text-gray-700'
              }`}>
                <input
                  type="checkbox"
                  checked={selectedReasons.includes('not_understood')}
                  onChange={() => handleReasonToggle('not_understood')}
                  className="sr-only"
                />
                <div className={`w-5 h-5 flex-shrink-0 rounded-md border ${
                  selectedReasons.includes('not_understood')
                    ? 'bg-orange-500 border-orange-500 flex items-center justify-center'
                    : isDarkMode
                      ? 'border-gray-600'
                      : 'border-gray-300'
                }`}>
                  {selectedReasons.includes('not_understood') && (
                    <CheckCircle size={14} className="text-white" />
                  )}
                </div>
                <span className="text-sm">{t.cancel_reason_not_understood}</span>
              </label>
              
              <label className={`flex items-center gap-3 p-3 rounded-lg cursor-pointer ${
                selectedReasons.includes('budget')
                  ? 'bg-orange-50 border border-orange-200 text-orange-700'
                  : isDarkMode
                    ? 'bg-[#2b2b2b] text-gray-300'
                    : 'bg-gray-50 text-gray-700'
              }`}>
                <input
                  type="checkbox"
                  checked={selectedReasons.includes('budget')}
                  onChange={() => handleReasonToggle('budget')}
                  className="sr-only"
                />
                <div className={`w-5 h-5 flex-shrink-0 rounded-md border ${
                  selectedReasons.includes('budget')
                    ? 'bg-orange-500 border-orange-500 flex items-center justify-center'
                    : isDarkMode
                      ? 'border-gray-600'
                      : 'border-gray-300'
                }`}>
                  {selectedReasons.includes('budget') && (
                    <CheckCircle size={14} className="text-white" />
                  )}
                </div>
                <span className="text-sm">{t.cancel_reason_budget}</span>
              </label>
              
              <label className={`flex items-center gap-3 p-3 rounded-lg cursor-pointer ${
                selectedReasons.includes('no_need')
                  ? 'bg-orange-50 border border-orange-200 text-orange-700'
                  : isDarkMode
                    ? 'bg-[#2b2b2b] text-gray-300'
                    : 'bg-gray-50 text-gray-700'
              }`}>
                <input
                  type="checkbox"
                  checked={selectedReasons.includes('no_need')}
                  onChange={() => handleReasonToggle('no_need')}
                  className="sr-only"
                />
                <div className={`w-5 h-5 flex-shrink-0 rounded-md border ${
                  selectedReasons.includes('no_need')
                    ? 'bg-orange-500 border-orange-500 flex items-center justify-center'
                    : isDarkMode
                      ? 'border-gray-600'
                      : 'border-gray-300'
                }`}>
                  {selectedReasons.includes('no_need') && (
                    <CheckCircle size={14} className="text-white" />
                  )}
                </div>
                <span className="text-sm">{t.cancel_reason_no_need}</span>
              </label>
              
              <label className={`flex items-center gap-3 p-3 rounded-lg cursor-pointer ${
                selectedReasons.includes('too_complicated')
                  ? 'bg-orange-50 border border-orange-200 text-orange-700'
                  : isDarkMode
                    ? 'bg-[#2b2b2b] text-gray-300'
                    : 'bg-gray-50 text-gray-700'
              }`}>
                <input
                  type="checkbox"
                  checked={selectedReasons.includes('too_complicated')}
                  onChange={() => handleReasonToggle('too_complicated')}
                  className="sr-only"
                />
                <div className={`w-5 h-5 flex-shrink-0 rounded-md border ${
                  selectedReasons.includes('too_complicated')
                    ? 'bg-orange-500 border-orange-500 flex items-center justify-center'
                    : isDarkMode
                      ? 'border-gray-600'
                      : 'border-gray-300'
                }`}>
                  {selectedReasons.includes('too_complicated') && (
                    <CheckCircle size={14} className="text-white" />
                  )}
                </div>
                <span className="text-sm">{t.cancel_reason_too_complicated}</span>
              </label>
              
              <label className={`flex items-start gap-3 p-3 rounded-lg cursor-pointer ${
                selectedReasons.includes('other')
                  ? 'bg-orange-50 border border-orange-200 text-orange-700'
                  : isDarkMode
                    ? 'bg-[#2b2b2b] text-gray-300'
                    : 'bg-gray-50 text-gray-700'
              }`}>
                <input
                  type="checkbox"
                  checked={selectedReasons.includes('other')}
                  onChange={() => handleReasonToggle('other')}
                  className="sr-only"
                />
                <div className={`w-5 h-5 flex-shrink-0 rounded-md border mt-1 ${
                  selectedReasons.includes('other')
                    ? 'bg-orange-500 border-orange-500 flex items-center justify-center'
                    : isDarkMode
                      ? 'border-gray-600'
                      : 'border-gray-300'
                }`}>
                  {selectedReasons.includes('other') && (
                    <CheckCircle size={14} className="text-white" />
                  )}
                </div>
                <div className="flex-1">
                  <span className="text-sm">{t.cancel_reason_other}</span>
                  {selectedReasons.includes('other') && (
                    <textarea
                      value={otherReason}
                      onChange={(e) => setOtherReason(e.target.value)}
                      placeholder={t.cancel_reason_specify}
                      className={`w-full mt-2 p-2 text-sm rounded-md ${
                        isDarkMode
                          ? 'bg-[#333333] text-gray-200 border-[#444444]'
                          : 'bg-white text-gray-700 border-gray-300'
                      } border`}
                      rows={2}
                    />
                  )}
                </div>
              </label>
            </div>
            
            <div className="flex justify-between mt-6">
              <button
                onClick={handleGoBack}
                className={`px-4 py-2 rounded-lg text-sm ${
                  isDarkMode 
                    ? 'text-gray-300 hover:bg-[#2b2b2b]' 
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                {t.back}
              </button>
              
              <button
                onClick={handleContinue}
                disabled={selectedReasons.length === 0}
                className={`px-4 py-2 rounded-lg text-sm font-medium ${
                  selectedReasons.length === 0
                    ? 'opacity-50 cursor-not-allowed bg-gray-300 text-gray-600'
                    : isDarkMode
                      ? 'bg-[#2b2b2b] text-gray-300 hover:bg-[#333333]'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {t.next}
              </button>
            </div>
          </div>
        )}
        
        {step === 3 && (
          <div className="space-y-4">
            <div className="bg-gradient-to-r from-orange-500 to-amber-500 p-4 rounded-lg text-white">
              <div className="flex items-center gap-2 mb-2">
                <Sparkles size={20} className="text-white" />
                <h4 className="font-semibold">{t.special_offer_title}</h4>
              </div>
              <p className="text-sm">{t.special_offer_description}</p>
              <div className="mt-3 text-center">
                <span className="text-xl font-bold">-50%</span>
                <span className="text-sm ml-1">{t.special_offer_duration}</span>
              </div>
            </div>
            
            {discountError && (
              <div className="p-3 bg-red-50 text-red-600 rounded-lg text-sm">
                {discountError}
              </div>
            )}
            
            <div className="flex flex-col gap-3 mt-6">
              <button
                onClick={handleApplyDiscount}
                disabled={isApplyingDiscount}
                className="w-full py-2.5 bg-orange-500 text-white rounded-lg font-medium hover:bg-orange-600 transition-colors flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isApplyingDiscount ? (
                  <>
                    <Loader2 size={16} className="animate-spin" />
                    {t.applying_discount}
                  </>
                ) : (
                  t.accept_special_offer
                )}
              </button>
              
              <button
                onClick={handleContinue}
                disabled={isLoading}
                className={`w-full py-2.5 rounded-lg font-medium transition-colors flex items-center justify-center gap-2 ${
                  isDarkMode 
                    ? 'bg-[#2b2b2b] text-gray-300 hover:bg-[#333333]' 
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                } disabled:opacity-50 disabled:cursor-not-allowed`}
              >
                {isLoading ? (
                  <>
                    <Loader2 size={16} className="animate-spin" />
                    {t.cancelling}
                  </>
                ) : (
                  t.confirm_cancellation
                )}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}